import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Original gallery card size (pre–Phase 4C). */
export const GALLERY_CARD_SIZE = 275;
/** Original gallery gap (pre–Phase 4C). */
export const GALLERY_CARD_GAP = 30;
/** Horizontal padding inside the gallery scroll area (matches original inline padding). */
export const GALLERY_GRID_PADDING = 20;

export type VirtualizedGalleryStripProps<T> = {
  items: T[];
  getItemId: (item: T) => string;
  /** Card width/height in px (default 275). */
  itemSize?: number;
  /** Gap between cards in px (default 30). */
  gap?: number;
  /** Extra rows rendered past each edge. */
  overscanRows?: number;
  className?: string;
  renderItem: (item: T, index: number) => React.ReactNode;
  /** @deprecated Prefer itemSize — kept for call-site compatibility. */
  itemWidth?: number;
  /** @deprecated Prefer itemSize. */
  itemHeight?: number;
  /** @deprecated Prefer overscanRows. */
  overscan?: number;
};

export type GridWindow = {
  columnCount: number;
  rowCount: number;
  startIndex: number;
  endIndex: number; // exclusive
  totalHeight: number;
  rowStride: number;
  colStride: number;
  offsetX: number;
};

/** Columns for CSS `repeat(auto-fill, itemSize)` with horizontal padding. */
export function computeColumnCount(
  containerWidth: number,
  itemSize: number,
  gap: number,
  paddingX: number = GALLERY_GRID_PADDING
): number {
  const usable = Math.max(itemSize, containerWidth - paddingX * 2);
  return Math.max(1, Math.floor((usable + gap) / (itemSize + gap)));
}

/**
 * 2D row window for a multi-row gallery.
 * scrollTop / viewportHeight are relative to the gallery track (not the page).
 */
export function computeGridWindow(args: {
  itemCount: number;
  columnCount: number;
  itemSize: number;
  gap: number;
  scrollTop: number;
  viewportHeight: number;
  overscanRows: number;
  containerWidth: number;
  paddingX?: number;
  paddingY?: number;
}): GridWindow {
  const {
    itemCount,
    itemSize,
    gap,
    scrollTop,
    viewportHeight,
    overscanRows,
    containerWidth,
    paddingX = GALLERY_GRID_PADDING,
    paddingY = GALLERY_GRID_PADDING,
  } = args;
  const columnCount = Math.max(1, args.columnCount);
  const rowStride = itemSize + gap;
  const colStride = itemSize + gap;
  const rowCount = itemCount === 0 ? 0 : Math.ceil(itemCount / columnCount);
  const gridWidth =
    columnCount === 0 ? 0 : columnCount * itemSize + Math.max(0, columnCount - 1) * gap;
  const usableWidth = Math.max(0, containerWidth - paddingX * 2);
  const offsetX = paddingX + Math.max(0, (usableWidth - gridWidth) / 2);
  const totalHeight =
    rowCount === 0
      ? paddingY * 2
      : paddingY * 2 + rowCount * itemSize + Math.max(0, rowCount - 1) * gap;

  if (itemCount === 0 || viewportHeight <= 0) {
    return {
      columnCount,
      rowCount,
      startIndex: 0,
      endIndex: 0,
      totalHeight,
      rowStride,
      colStride,
      offsetX,
    };
  }

  const contentScroll = Math.max(0, scrollTop - paddingY);
  let startRow = Math.floor(contentScroll / rowStride) - overscanRows;
  startRow = Math.max(0, startRow);
  const visibleRows = Math.ceil(viewportHeight / rowStride) + 1;
  let endRow = Math.min(rowCount, startRow + visibleRows + overscanRows * 2);
  if (endRow === rowCount) {
    startRow = Math.max(0, endRow - (visibleRows + overscanRows * 2));
  }

  const startIndex = startRow * columnCount;
  const endIndex = Math.min(itemCount, endRow * columnCount);

  return {
    columnCount,
    rowCount,
    startIndex,
    endIndex,
    totalHeight,
    rowStride,
    colStride,
    offsetX,
  };
}

/**
 * @deprecated Horizontal strip math — kept so older tests/tools still compile.
 * Prefer computeGridWindow for the multi-row gallery.
 */
export function computeHorizontalWindow(args: {
  itemCount: number;
  itemWidth: number;
  gap: number;
  scrollLeft: number;
  viewportWidth: number;
  overscan: number;
}): {
  startIndex: number;
  endIndex: number;
  totalWidth: number;
  stride: number;
} {
  const { itemCount, itemWidth, gap, scrollLeft, viewportWidth, overscan } = args;
  const stride = itemWidth + gap;
  const totalWidth =
    itemCount === 0 ? 0 : itemCount * itemWidth + Math.max(0, itemCount - 1) * gap;
  if (itemCount === 0 || viewportWidth <= 0 || stride <= 0) {
    return { startIndex: 0, endIndex: 0, totalWidth, stride };
  }
  const rawStart = Math.floor(scrollLeft / stride) - overscan;
  let startIndex = Math.max(0, rawStart);
  const visibleCount = Math.ceil(viewportWidth / stride) + 1;
  let endIndex = Math.min(itemCount, startIndex + visibleCount + overscan * 2);
  if (endIndex === itemCount) {
    startIndex = Math.max(0, endIndex - (visibleCount + overscan * 2));
  }
  return { startIndex, endIndex, totalWidth, stride };
}

/**
 * Multi-row virtualized gallery (CSS grid visual: auto-fill 275px / 30px gap).
 * Only mounts cards near the visible viewport. Unmounting must NOT revoke blobs (Phase 4A).
 */
export function VirtualizedGalleryStrip<T>(props: VirtualizedGalleryStripProps<T>) {
  const {
    items,
    getItemId,
    gap = GALLERY_CARD_GAP,
    className = 'EOL-gallery-grid-scroll',
    renderItem,
  } = props;
  const itemSize = props.itemSize ?? props.itemWidth ?? props.itemHeight ?? GALLERY_CARD_SIZE;
  const overscanRows = props.overscanRows ?? props.overscan ?? 2;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [containerWidth, setContainerWidth] = useState(1280);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setContainerWidth(el.clientWidth || rect.width || 1280);
    // Visible slice of this gallery relative to its top (page scroll).
    const visibleTop = Math.max(0, -rect.top);
    setScrollTop(visibleTop);
    setViewportHeight(typeof window !== 'undefined' ? window.innerHeight : 800);
  }, []);

  useEffect(() => {
    measure();
    const onScroll = () => measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    let ro: ResizeObserver | null = null;
    const el = scrollRef.current;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (ro) ro.disconnect();
    };
  }, [measure, items.length]);

  const columnCount = useMemo(
    () => computeColumnCount(containerWidth, itemSize, gap, GALLERY_GRID_PADDING),
    [containerWidth, itemSize, gap]
  );

  const windowed = useMemo(
    () =>
      computeGridWindow({
        itemCount: items.length,
        columnCount,
        itemSize,
        gap,
        scrollTop,
        viewportHeight,
        overscanRows,
        containerWidth,
      }),
    [
      items.length,
      columnCount,
      itemSize,
      gap,
      scrollTop,
      viewportHeight,
      overscanRows,
      containerWidth,
    ]
  );

  const visibleItems = useMemo(() => {
    const out: Array<{ item: T; index: number; left: number; top: number }> = [];
    for (let index = windowed.startIndex; index < windowed.endIndex; index++) {
      const row = Math.floor(index / windowed.columnCount);
      const col = index % windowed.columnCount;
      out.push({
        item: items[index],
        index,
        left: windowed.offsetX + col * windowed.colStride,
        top: GALLERY_GRID_PADDING + row * windowed.rowStride,
      });
    }
    return out;
  }, [items, windowed]);

  return (
    <div
      ref={scrollRef}
      className={className}
      data-virtualized-gallery="1"
      data-virtualized-gallery-layout="grid"
      data-virtualized-gallery-columns={windowed.columnCount}
    >
      <div
        data-virtualized-gallery-track="1"
        style={{
          position: 'relative',
          width: '100%',
          height: windowed.totalHeight,
          boxSizing: 'border-box',
        }}
      >
        {visibleItems.map(({ item, index, left, top }) => (
          <div
            key={getItemId(item)}
            data-virtualized-gallery-item={getItemId(item)}
            data-virtualized-gallery-index={index}
            style={{
              position: 'absolute',
              top,
              left,
              width: itemSize,
              height: itemSize,
            }}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
}
